import { Component, OnInit, OnDestroy } from '@angular/core'
import { Router } from '@angular/router'
import { AdminService } from '../../../services/admin.service'
import { AdminChangeLog, AngularRoutes, ChangeLogsEntityEnum, ContractStatus, EnumNames, Subscription, SubscriptionEventActor, subscriptionsPath, SubscriptionType } from '@parkupp/core'
import { arrayUnion, collection, collectionData, deleteDoc, doc, orderBy, query, serverTimestamp, Timestamp, updateDoc, where, Query, DocumentData } from '@angular/fire/firestore'
import { GlobalService } from '../../../services/global.service'
import * as moment from 'moment'
import Swal from 'sweetalert2'
import { BackendService } from '../../../services/backend.service'
import { SubscriptionService } from '../../../services/subscription.service'
import { AdminChangeLogService } from '../../../services/admin-change-log.service'
import { AuthenticationService } from '../../../services/authentication.service'
import { ClientService } from '../../../services/client.service'
import { ParkingService } from '../../../services/parking.service'
import { EnterpriseContextService } from '../../../services/enterprise-context.service'
import { Subscription as RxSubscription } from 'rxjs'
import { SubscriptionEventLogService } from '../../../services/subscription-event-log.service'

interface TableColumn {
    key: string
    label: string
    sortable: boolean
    width?: string
    cssClass?: string
}

@Component({
    selector: 'app-admin-subscriptions-page',
    templateUrl: './admin-list-subscriptions-page.component.html',
    styleUrls: ['./admin-list-subscriptions-page.component.scss'],
})
export class AdminListSubscriptionsPageComponent implements OnInit, OnDestroy {
    EnumNames = EnumNames
    ContractStatus = ContractStatus
    subscriptions: Subscription[]
    
    // Enterprise context subscription
    private enterpriseSubscription: RxSubscription
    allSubscriptionCounts = { active: 0, cancelled: 0, completed: 0 }

    filteredSubscriptions: Subscription[]

    // Column configuration
    columns: TableColumn[] = [
        { key: 'createdAt', label: 'Created', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'type', label: 'Type', sortable: true },
        { key: 'term', label: 'Term', sortable: true },
        { key: 'details', label: 'Details', sortable: false },
        { key: 'clientRef', label: 'Client', sortable: true },
        { key: 'merchantRef', label: 'Portfolio', sortable: true },
        { key: 'parkingRef', label: 'Parking', sortable: true },
        { key: 'baysBooked', label: 'Bays', sortable: true },
        { key: 'startDate', label: 'Start', sortable: true },
        { key: 'endDate', label: 'End', sortable: true },
        { key: 'period', label: 'Duration', sortable: true },
        { key: 'periodFee', label: 'Monthly Fee', sortable: true },
        { key: 'totalValue', label: 'Total Value', sortable: true },
        { key: 'nextDebitDate', label: 'Invoice Due Date', sortable: true },
        { key: 'renewalStatus', label: 'Renewal Status', sortable: true },
        { key: 'reviewRequested', label: 'Review', sortable: true },
        { key: 'p_source', label: 'Source', sortable: true },
        { key: 'p_campaign', label: 'Campaign', sortable: true },
        { key: 'admytTenantId', label: 'Admyt Tenant ID', sortable: true },
    ]

    // Sorting state
    currentSortColumn: string = 'createdAt'
    currentSortDirection: 'asc' | 'desc' = 'desc'

    activeContractStatuses: ContractStatus[] = [
        ContractStatus.new,
        ContractStatus.paymentInitiated,
        ContractStatus.active,
        ContractStatus.bouncing,
        ContractStatus.renewing,
        ContractStatus.pendingClientApproval,
    ]
    cancelledContractStatuses: ContractStatus[] = [
        ContractStatus.paymentFailed,
        ContractStatus.clientCancelled,
        ContractStatus.merchantRejected,
        ContractStatus.clientBreachedNonPayment,
        ContractStatus.merchantBreached,
        ContractStatus.mutuallyTerminated,
        ContractStatus.expired,
    ]
    completedContractStatuses: ContractStatus[] = [ContractStatus.completed]
    currentStatusFilter: string = 'active'
    today: number = moment().add(7, 'days').toDate().getTime()
    AngularRoutes = AngularRoutes
    SubscriptionType = SubscriptionType
    selectedSubscription: Subscription

    // Column configuration
    showColumnConfig: boolean = false
    visibleColumns: { [key: string]: boolean } = {}

    constructor(
        public globalService: GlobalService,
        private subscriptionService: SubscriptionService,
        private backendService: BackendService,
        private adminService: AdminService,
        private adminChangeLogService: AdminChangeLogService,
        private authService: AuthenticationService,
        private clientService: ClientService,
        private parkingService: ParkingService,
        private eventLogService: SubscriptionEventLogService,
        private router: Router,
        public enterpriseContext: EnterpriseContextService
    ) {}

    ngOnInit(): void {
        this.loadColumnPreferences()
        this.fetchAllCounts()
        this.runSearch()
        
        // Subscribe to enterprise context changes to refresh data
        this.enterpriseSubscription = this.enterpriseContext.currentEnterprise$.subscribe(() => {
            this.fetchAllCounts()
            this.runSearch()
        })
    }
    
    ngOnDestroy(): void {
        if (this.enterpriseSubscription) {
            this.enterpriseSubscription.unsubscribe()
        }
    }

    async fetchAllCounts() {
        // Fetch counts for all status categories
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        
        let activeRef: Query<DocumentData> = query(collection(this.adminService.firestore, subscriptionsPath()), where('status', 'in', this.activeContractStatuses))
        let cancelledRef: Query<DocumentData> = query(collection(this.adminService.firestore, subscriptionsPath()), where('status', 'in', this.cancelledContractStatuses))
        let completedRef: Query<DocumentData> = query(collection(this.adminService.firestore, subscriptionsPath()), where('status', 'in', this.completedContractStatuses))

        // Apply enterprise filter if set
        if (enterpriseKey) {
            activeRef = query(activeRef, where('denormEnterpriseKey', '==', enterpriseKey))
            cancelledRef = query(cancelledRef, where('denormEnterpriseKey', '==', enterpriseKey))
            completedRef = query(completedRef, where('denormEnterpriseKey', '==', enterpriseKey))
        }

        collectionData(activeRef).subscribe((docs) => {
            this.allSubscriptionCounts.active = docs.length
        })
        collectionData(cancelledRef).subscribe((docs) => {
            this.allSubscriptionCounts.cancelled = docs.length
        })
        collectionData(completedRef).subscribe((docs) => {
            this.allSubscriptionCounts.completed = docs.length
        })
    }

    setStatusFilter(status: string) {
        this.currentStatusFilter = status
        this.runSearch()
    }

    runSearch() {
        let statuses: ContractStatus[]
        if (this.currentStatusFilter == 'completed') {
            statuses = this.completedContractStatuses
        } else if (this.currentStatusFilter == 'cancelled') {
            statuses = this.cancelledContractStatuses
        } else {
            statuses = this.activeContractStatuses
        }
        
        const enterpriseKey = this.enterpriseContext.currentEnterpriseKey
        
        let ref: Query<DocumentData> = query(
            collection(this.adminService.firestore, subscriptionsPath()), 
            orderBy('createdAt', 'desc'), 
            where('status', 'in', statuses)
        )
        
        // Apply enterprise filter if set
        if (enterpriseKey) {
            ref = query(ref, where('denormEnterpriseKey', '==', enterpriseKey))
        }
        
        collectionData(ref, { idField: 'documentId' }).subscribe((documents: any[]) => {
            this.subscriptions = []
            documents.forEach((documentData) => {
                let subscription = new Subscription(documentData)
                subscription.$key = documentData.documentId

                this.subscriptions.push(subscription)
            })
            this.filteredSubscriptions = this.subscriptions // Initialize filteredSubscriptions
            this.applySorting()
        })
    }

    changeStatus(e: any) {
        this.currentStatusFilter = e?.target?.value
        this.runSearch()
    }

    searchKeydown($event: any) {
        const input = ($event.target as HTMLInputElement).value.toLowerCase()
        this.filteredSubscriptions = this.subscriptions.filter((subscription) => {
            return (
                subscription.denormMerchantCompanyName?.toLowerCase().includes(input) ||
                subscription.denormClientFirstName?.toLowerCase().includes(input) ||
                subscription.denormClientLastName?.toLowerCase().includes(input) ||
                subscription.denormParkingTitle?.toLowerCase().includes(input)
            )
        })
        this.applySorting()
    }

    async deleteSubscription(subscription: Subscription) {
        // Accepted requests whose first payment is still processing are cancelled from the
        // subscription page, which voids any open authorisation and emails both parties
        if (subscription.isAcceptedPaymentProcessing) {
            await Swal.fire({
                icon: 'info',
                title: 'Payment is processing',
                text: 'The landlord has accepted this request. Use Cancel request on the subscription page instead.',
            })
            return
        }

        const { value: rejectionReason } = await Swal.fire({
            input: 'text',
            title: 'Enter cancellation Reason',
            inputPlaceholder: 'Enter reason',
            showCancelButton: true,
        })

        if (rejectionReason) {
            const user = await this.authService.waitForUid()
            const adminUser = await this.clientService.getPromise(user.uid)
            const adminName = `${adminUser?.firstName || ''} ${adminUser?.lastName || ''}`.trim() || 'Admin'

            const adminCancelEvent = {
                timestamp: Timestamp.now(),
                eventType: 'admin_cancelled',
                description: `Cancelled by admin: ${rejectionReason}`,
                actor: 'admin',
                actorDetails: {
                    name: adminName,
                    adminKey: user.uid,
                    ...(adminUser?.$email ? { email: adminUser.$email } : {}),
                },
                source: 'admin-portal',
                metadata: {
                    rejectionReason: rejectionReason,
                    previousStatus: subscription.status,
                },
            }

            const subscriptionDocRef = doc(this.adminService.firestore, subscriptionsPath(), subscription.$key!)
            await updateDoc(subscriptionDocRef, {
                eventLog: arrayUnion(adminCancelEvent),
                status: ContractStatus.clientCancelled,
                rejectionReason: rejectionReason,
                updatedAt: serverTimestamp(),
            })

            subscription.status = ContractStatus.clientCancelled
            subscription.rejectionReason = rejectionReason

            const subscriptionRef = this.subscriptionService.getDocRef(subscription.$key!)

            const adminRef = await this.clientService.getDocRef(user.uid)
            const changeLog = new AdminChangeLog({
                updatedEntity: ChangeLogsEntityEnum.SUBSCRIPTION,
                subscriptionRef: subscriptionRef as any,
                clientRef: subscription.clientRef as any,
                merchantRef: subscription.merchantRef as any,
                comment: `Subscription cancelled by admin for reason: ${rejectionReason}`,
                createdAt: serverTimestamp() as Timestamp,
                adminRef: adminRef as any,
            })
            await this.adminChangeLogService.addOrUpdate(changeLog)

            // Send the input to the backend service
            await this.backendService.httpSendSlack({
                message: `*Subscription Cancelled by Admin* - ${subscription.denormClientFirstName} ${subscription.denormClientLastName} - ${rejectionReason}`,
            })
        }
    }

    async deleteManualSubscription(subscription: Subscription) {
        const result = await Swal.fire({
            title: 'Delete Manual Subscription?',
            text: 'This will permanently delete this manual subscription. This action cannot be undone.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Yes, delete it',
            cancelButtonText: 'Cancel'
        })

        if (result.isConfirmed) {
            try {
                await deleteDoc(doc(this.adminService.firestore, subscriptionsPath(), subscription.$key!))
                await Swal.fire({
                    title: 'Deleted!',
                    text: 'The manual subscription has been deleted.',
                    icon: 'success',
                    timer: 2000,
                    showConfirmButton: false
                })
                // Refresh the list
                this.runSearch()
            } catch (error) {
                console.error('Error deleting subscription:', error)
                await Swal.fire({
                    title: 'Error',
                    text: 'Failed to delete the subscription. Please try again.',
                    icon: 'error'
                })
            }
        }
    }

    /**
     * Converting the payment mechanism is a deliberate, confirmed migration — never a
     * side effect of editing a subscription. Always audited.
     */
    async convertPaymentMechanism(subscription: Subscription, newType: SubscriptionType) {
        const previousType = subscription.type
        const previousName = subscription.paymentMechanismName

        const confirmation = await Swal.fire({
            title: 'Change payment mechanism?',
            html: `This subscription will be moved from <strong>${previousName}</strong> to <strong>${EnumNames.getSubscriptionType(newType)}</strong>. Future billing will follow the new mechanism.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, convert it',
            cancelButtonText: 'Cancel',
        })

        if (!confirmation.isConfirmed) {
            return
        }

        subscription.type = newType
        if (newType == SubscriptionType.EFT && !subscription.nextDebitDate) {
            const nextDebitDate = this.getNextDebitDate(subscription)
            if (nextDebitDate) {
                subscription.nextDebitDate = nextDebitDate
            }
        }

        // Audit entry rides along in the same write as the conversion.
        const auditEvent = await this.eventLogService.buildChangeEvent(
            SubscriptionEventActor.ADMIN,
            this.eventLogService.singleChange('type', 'Payment Mechanism', previousType, newType),
            {
                source: 'admin-portal',
                summary: `Payment mechanism converted from ${previousName} to ${subscription.paymentMechanismName} by admin`,
            }
        )
        await this.subscriptionService.updateSubscription(subscription, auditEvent)

        const user = await this.authService.waitForUid()
        const adminRef = await this.clientService.getDocRef(user.uid)
        await this.adminChangeLogService.addOrUpdate(
            new AdminChangeLog({
                updatedEntity: ChangeLogsEntityEnum.SUBSCRIPTION,
                subscriptionRef: this.subscriptionService.getDocRef(subscription.$key!) as any,
                clientRef: subscription.clientRef as any,
                merchantRef: subscription.merchantRef as any,
                comment: `Payment mechanism converted from ${previousName} to ${subscription.paymentMechanismName}`,
                createdAt: serverTimestamp() as Timestamp,
                adminRef: adminRef as any,
            })
        )
    }

    async reactivateSubscription(subscription: Subscription) {
        const subscriptionRef = this.subscriptionService.getDocRef(subscription.$key!)
        const user = await this.authService.waitForUid()
        const adminRef = await this.clientService.getDocRef(user.uid)
        const changeLog = new AdminChangeLog({
            updatedEntity: ChangeLogsEntityEnum.SUBSCRIPTION,
            subscriptionRef: subscriptionRef as any,
            clientRef: subscription.clientRef as any,
            merchantRef: subscription.merchantRef as any,
            comment: `Subscription reactivated by admin`,
            createdAt: serverTimestamp() as Timestamp,
            adminRef: adminRef as any,
        })
        await this.adminChangeLogService.addOrUpdate(changeLog)

        const previousStatus = subscription.status
        const auditEvent = await this.eventLogService.buildChangeEvent(
            SubscriptionEventActor.ADMIN,
            this.eventLogService.singleChange('status', 'Status', previousStatus, ContractStatus.active),
            {
                source: 'admin-portal',
                summary: `Subscription reactivated by admin (was ${previousStatus})`,
            }
        )

        subscription.status = ContractStatus.active
        await this.subscriptionService.updateSubscription(subscription, auditEvent)
    }

    async showContextMenu(event: MouseEvent, subscription: Subscription) {
        this.globalService.contextMenuItems = []
        this.selectedSubscription = subscription
        this.globalService.showContextMenu(event)
        // this.globalService.contextMenuItems.push({
        //     icon: 'fa fa-credit-card',
        //     label: 'Set Type to PayGate',
        //     action: () => {
        //         this.selectedSubscription.type = SubscriptionType.PAYGATE
        //         this.subscriptionService.updateSubscription(this.selectedSubscription)
        //     },
        // })
        // this.globalService.contextMenuItems.push({
        //     icon: 'fa fa-file-invoice',
        //     label: 'Set Type to PayFast',
        //     action: () => {
        //         this.selectedSubscription.type = SubscriptionType.PAYFAST
        //         this.subscriptionService.updateSubscription(this.selectedSubscription)
        //     },
        // })
        // this.globalService.contextMenuItems.push({
        //     icon: 'fa fa-bank',
        //     label: 'Set Type to EFT',
        //     action: () => {
        //         this.selectedSubscription.type = SubscriptionType.EFT
        //         this.subscriptionService.updateSubscription(this.selectedSubscription)
        //     },
        // })

        // For MANUAL subscriptions: only show Edit and Delete
        if (this.selectedSubscription.type == SubscriptionType.MANUAL) {
            this.globalService.contextMenuItems.push({
                icon: 'fa fa-edit',
                label: 'Edit Manual Subscription',
                action: () => {
                    window.location.href = `/admin/subscriptions-manual/edit/${this.selectedSubscription.$key}`
                },
            })
            this.globalService.contextMenuItems.push({
                icon: 'fa fa-trash',
                label: 'Delete Manual Subscription',
                action: async () => {
                    await this.deleteManualSubscription(this.selectedSubscription)
                },
            })
            return // Don't show other options for manual subscriptions
        }

        // For non-MANUAL subscriptions: show all other options
        let convertToMenuItems: any = []
        if (this.selectedSubscription.type != SubscriptionType.EFT) {
            convertToMenuItems.push({
                label: 'Convert to EFT',
                action: () => this.convertPaymentMechanism(this.selectedSubscription, SubscriptionType.EFT),
            })
        }

        if (this.selectedSubscription.type != SubscriptionType.PAYGATE) {
            convertToMenuItems.push({
                label: 'Convert to PayGate',
                action: () => this.convertPaymentMechanism(this.selectedSubscription, SubscriptionType.PAYGATE),
            })
        }

        if (this.selectedSubscription.type != SubscriptionType.PAYFAST) {
            convertToMenuItems.push({
                label: 'Convert to PayFast',
                action: () => this.convertPaymentMechanism(this.selectedSubscription, SubscriptionType.PAYFAST),
            })
        }

        this.globalService.contextMenuItems.push({
            icon: 'fa fa-exchange',
            label: 'Convert Payment Method: ',
            subMenuItems: convertToMenuItems,
        })

        if (this.selectedSubscription.status == ContractStatus.active) {
            this.globalService.contextMenuItems.push({
                icon: 'fa fa-trash',
                label: 'Mark Subscription as Client Cancelled',
                action: async () => {
                    await this.deleteSubscription(this.selectedSubscription)
                },
            })
        }

        // PayGate/PayFast subscriptions are backed by a live payment mandate, so only the
        // manually-administered ones (EFT) can be edited after the fact.
        if (this.selectedSubscription.isAdminEditable) {
            this.globalService.contextMenuItems.push({
                icon: 'fa fa-edit',
                label: `Edit ${this.selectedSubscription.paymentMechanismName} Details`,
                action: () => {
                    window.location.href = `/admin/subscriptions-manual/edit/${this.selectedSubscription.$key}`
                },
            })
        }
    }

    getNextDebitDate(subscription: Subscription) {
        const startDate = subscription.startDate.toDate()
        const endDate = subscription.endDate.toDate()
        let nextDebitDate = new Date(startDate)
        nextDebitDate.setMonth(nextDebitDate.getMonth() + 1)

        const currentDate = new Date()
        while (nextDebitDate <= currentDate) {
            nextDebitDate.setMonth(nextDebitDate.getMonth() + 1)
        }

        if (nextDebitDate < endDate) {
            return Timestamp.fromDate(nextDebitDate)
        } else {
            return null
        }
    }

    // Sorting methods
    sortByColumn(columnKey: string): void {
        if (this.currentSortColumn === columnKey) {
            // Toggle direction if same column
            this.currentSortDirection = this.currentSortDirection === 'asc' ? 'desc' : 'asc'
        } else {
            // New column, default to ascending
            this.currentSortColumn = columnKey
            this.currentSortDirection = 'asc'
        }

        this.applySorting()
    }

    applySorting(): void {
        if (!this.filteredSubscriptions) return

        this.filteredSubscriptions.sort((a, b) => {
            let aValue: any
            let bValue: any

            // Get values based on column key
            switch (this.currentSortColumn) {
                case 'createdAt':
                    aValue = a.createdAt?.toDate?.() || a.createdAt
                    bValue = b.createdAt?.toDate?.() || b.createdAt
                    break
                case 'status':
                    aValue = a.statusNiceName || a.status
                    bValue = b.statusNiceName || b.status
                    break
                case 'type':
                    aValue = EnumNames.getSubscriptionType(a.type)
                    bValue = EnumNames.getSubscriptionType(b.type)
                    break
                case 'manualClientName':
                    aValue = a.manualClientName || ''
                    bValue = b.manualClientName || ''
                    break
                case 'clientRef':
                    aValue = `${a.denormClientFirstName || ''} ${a.denormClientLastName || ''}`.trim()
                    bValue = `${b.denormClientFirstName || ''} ${b.denormClientLastName || ''}`.trim()
                    break
                case 'merchantRef':
                    aValue = a.denormMerchantCompanyName || ''
                    bValue = b.denormMerchantCompanyName || ''
                    break
                case 'parkingRef':
                    aValue = a.denormParkingTitle || ''
                    bValue = b.denormParkingTitle || ''
                    break
                case 'baysBooked':
                    aValue = a.baysBooked || 0
                    bValue = b.baysBooked || 0
                    break
                case 'startDate':
                    aValue = a.startDate?.toDate?.() || a.startDate
                    bValue = b.startDate?.toDate?.() || b.startDate
                    break
                case 'endDate':
                    aValue = a.endDate?.toDate?.() || a.endDate
                    bValue = b.endDate?.toDate?.() || b.endDate
                    break
                case 'period':
                    aValue = a.period || 0
                    bValue = b.period || 0
                    break
                case 'periodFee':
                    aValue = a.periodFee || 0
                    bValue = b.periodFee || 0
                    break
                case 'totalValue':
                    aValue = (a.periodFee || 0) * (a.period || 1)
                    bValue = (b.periodFee || 0) * (b.period || 1)
                    break
                case 'nextDebitDate':
                    aValue = a.nextDebitDate?.toDate?.() || a.nextDebitDate
                    bValue = b.nextDebitDate?.toDate?.() || b.nextDebitDate
                    break
                case 'renewalStatus':
                    aValue = a.renewalStatus || ''
                    bValue = b.renewalStatus || ''
                    break
                case 'admytTenantId':
                    aValue = a.admytTenantId || ''
                    bValue = b.admytTenantId || ''
                    break
                case 'reviewRequested':
                    aValue = a.reviewRequested ? 1 : 0
                    bValue = b.reviewRequested ? 1 : 0
                    break
                default:
                    aValue = a[this.currentSortColumn as keyof Subscription]
                    bValue = b[this.currentSortColumn as keyof Subscription]
            }

            // Handle null/undefined values
            if (aValue === null || aValue === undefined) aValue = ''
            if (bValue === null || bValue === undefined) bValue = ''

            // Compare values
            let comparison = 0
            if (aValue < bValue) comparison = -1
            if (aValue > bValue) comparison = 1

            // Apply sort direction
            return this.currentSortDirection === 'asc' ? comparison : -comparison
        })
    }

    getSortIcon(columnKey: string): string {
        if (!columnKey || this.currentSortColumn !== columnKey) {
            return 'fa fa-sort'
        }
        return this.currentSortDirection === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down'
    }

    isColumnSorted(columnKey: string): boolean {
        return this.currentSortColumn === columnKey
    }

    getFullNameFromSubscription(subscription: Subscription) {
        return `${subscription.denormClientFirstName || ''} ${subscription.denormClientLastName || ''}`.trim()
    }

    async openAdmytTenant(subscription: Subscription) {
        const parking = await this.parkingService.getPromise(subscription.parkingRef.id)
        if (parking.admytCenter) {
            window.open(`http://localhost:4200/admin/admyt-center/${parking.admytCenter}/tenant/${subscription.admytTenantId}`, '_blank')
        }
    }

    navigateToReporting(source: string | undefined, campaign: string | undefined) {
        const queryParams: any = {}
        if (source) {
            queryParams.source = source
        }
        if (campaign) {
            queryParams.campaign = campaign
        }
        this.router.navigate(['/admin/reporting'], { queryParams })
    }

    // Stats card computed properties
    getActiveCount(): number {
        return this.allSubscriptionCounts.active
    }

    getCancelledCount(): number {
        return this.allSubscriptionCounts.cancelled
    }

    getCompletedCount(): number {
        return this.allSubscriptionCounts.completed
    }

    getTotalMonthlyRevenue(): number {
        if (!this.subscriptions || this.currentStatusFilter !== 'active') return 0
        return this.subscriptions.reduce((sum, sub) => sum + (sub.periodFee || 0), 0) / 100
    }

    openSubscriptionDetail(subscription: Subscription) {
        window.open(`/admin/subscription/${subscription.$key}`, '_blank')
    }

    // Column configuration methods
    loadColumnPreferences(): void {
        const saved = this.getCookie('subscriptionColumns')
        if (saved) {
            try {
                const parsed = JSON.parse(saved)
                this.columns.forEach((col) => {
                    this.visibleColumns[col.key] = parsed[col.key] !== undefined ? parsed[col.key] : true
                })
            } catch {
                this.setDefaultColumns()
            }
        } else {
            this.setDefaultColumns()
        }
    }

    setDefaultColumns(): void {
        this.columns.forEach((col) => {
            this.visibleColumns[col.key] = true
        })
    }

    saveColumnPreferences(): void {
        this.setCookie('subscriptionColumns', JSON.stringify(this.visibleColumns), 365)
    }

    openColumnConfig(): void {
        this.showColumnConfig = true
    }

    closeColumnConfig(): void {
        this.showColumnConfig = false
        this.saveColumnPreferences()
    }

    toggleColumn(key: string): void {
        this.visibleColumns[key] = !this.visibleColumns[key]
        this.saveColumnPreferences()
    }

    selectAllColumns(): void {
        this.columns.forEach((col) => {
            this.visibleColumns[col.key] = true
        })
        this.saveColumnPreferences()
    }

    deselectAllColumns(): void {
        this.columns.forEach((col) => {
            this.visibleColumns[col.key] = false
        })
        this.saveColumnPreferences()
    }

    // Cookie helpers
    private getCookie(name: string): string {
        const nameEQ = name + '='
        const ca = document.cookie.split(';')
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i]
            while (c.charAt(0) === ' ') c = c.substring(1, c.length)
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length)
        }
        return ''
    }

    private setCookie(name: string, value: string, days: number): void {
        let expires = ''
        if (days) {
            const date = new Date()
            date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000)
            expires = '; expires=' + date.toUTCString()
        }
        document.cookie = name + '=' + (value || '') + expires + '; path=/'
    }
}
